/**
 * One decision, shared by all four frontends: what a screen shows for a query.
 *
 * The family this closes was found in every app at once. A screen gates its
 * whole render on `query.isPending`, so the error branch three lines below -
 * which was already written, with its retry button and its way back - is
 * unreachable, because `status` stays `'pending'` for the WHOLE LIFE of a query
 * that never produced data. Two ways that happens, both observed:
 *
 *  - TanStack's retryer re-evaluates `focusManager.isFocused() && (networkMode
 *    === 'always' || onlineManager.isOnline())` before resuming a retry it has
 *    already agreed to. When that is false the query does not fail, it PARKS at
 *    `status: 'pending' / fetchStatus: 'paused'` and waits for an event that may
 *    never come. A guest sat on that spinner for 90+ seconds with
 *    `navigator.onLine` true, one request in the server log and an empty
 *    console.
 *  - A fetch that claims to be running and never lands. Nothing settles it, so
 *    nothing renders but the spinner.
 *
 * No screen in any app may therefore branch on `isPending` directly. It asks
 * for a PHASE, and the phase is total: exactly one of pending / error / empty /
 * ready, with `pending` bounded by a deadline. `<QueryBoundary>` in
 * `query-boundary.tsx` is the component form, and it takes the error arm as a
 * required prop so a screen physically cannot be written without one.
 *
 * Pure and framework-free on purpose - no React, no TanStack import, no error
 * classification (each app owns what its own failures mean). The one thing that
 * needs a timer, "has this fetch run past the reader's patience", is passed in
 * as a boolean and lives in the hook next door.
 */

/** The four states a screen can be in. There is no fifth, and no absence of one. */
export type QueryPhase = 'pending' | 'error' | 'empty' | 'ready';

/**
 * How long a genuinely in-flight fetch may run before the screen stops calling
 * it "loading" and offers a way out instead.
 *
 * Long enough that a slow morning connection still resolves normally, short
 * enough that nobody is left staring. Nothing is cancelled when it elapses: a
 * request that lands later still resolves; the deadline only decides what is
 * SHOWN.
 */
export const RESOLVE_PATIENCE_MS = 12_000;

/** The part of a TanStack query result this decision reads. Nothing more. */
export interface QueryLike<T> {
  readonly status: 'pending' | 'error' | 'success';
  /** `'fetching'` means a request is genuinely in flight right now. */
  readonly fetchStatus: 'fetching' | 'paused' | 'idle';
  readonly data: T | undefined;
  readonly error: unknown;
  /** The failure of the attempt that just failed, while a retry is still pending. */
  readonly failureReason?: unknown;
}

export interface QueryPhaseOptions<T> {
  /** True once an in-flight fetch has run past `RESOLVE_PATIENCE_MS`. */
  readonly stalled?: boolean;
  /** True when the response is well-formed but has nothing in it. */
  readonly isEmpty?: (data: T) => boolean;
  /**
   * A query switched off by `enabled: false` is waiting for its own
   * precondition, not for the network. It reads as `pending` - the screen is
   * showing something else in the meantime - and the deadline does not apply.
   */
  readonly disabled?: boolean;
}

/**
 * A DISCRIMINATED union, not a record with optional fields: `state.data` is only
 * reachable once the phase says there is data, so the `data === undefined`
 * fall-through that used to end in a second, unreachable spinner cannot be
 * written.
 */
export type QueryState<T> =
  | { readonly phase: 'pending' }
  | { readonly phase: 'error'; readonly error: unknown }
  | { readonly phase: 'empty'; readonly data: T }
  | { readonly phase: 'ready'; readonly data: T };

/**
 * The whole decision table.
 *
 *  - `success`             -> `ready`, or `empty` when the payload says so.
 *                            Stays ready through a failed BACKGROUND refetch:
 *                            a reader already looking at data must not have it
 *                            replaced by an error card.
 *  - `error`               -> `error`.
 *  - `pending` + fetching  -> `pending`, until `stalled`, then `error`.
 *  - `pending` + paused    -> `error`. Nothing is coming: the retryer parked it
 *                            and no further request will be sent.
 *  - `pending` + idle      -> `error`, unless the query is `disabled`, in which
 *                            case it is honestly still pending: an `enabled:
 *                            false` query has not been asked yet.
 */
export function queryPhase<T>(
  query: QueryLike<T>,
  options: QueryPhaseOptions<T> = {},
): QueryState<T> {
  if (query.status === 'success' && query.data !== undefined) {
    const empty = options.isEmpty?.(query.data) === true;
    return { phase: empty ? 'empty' : 'ready', data: query.data };
  }
  if (query.status === 'error') {
    return { phase: 'error', error: query.error ?? query.failureReason };
  }
  if (options.disabled === true) {
    return { phase: 'pending' };
  }
  if (query.fetchStatus === 'fetching' && options.stalled !== true) {
    return { phase: 'pending' };
  }
  // Paused, cancelled, or in flight for longer than anyone should wait. There is
  // no error object in the paused case - the screen renders its generic
  // "could not reach the service" arm, which is the honest answer.
  return { phase: 'error', error: query.error ?? query.failureReason };
}

/**
 * True when a query is genuinely in flight, which is the only condition the
 * patience deadline should be started for.
 */
export function isResolving<T>(query: QueryLike<T>): boolean {
  return query.status === 'pending' && query.fetchStatus === 'fetching';
}

/**
 * TWO READS A SCREEN NEEDS BOTH OF, as ONE query for the phase machine.
 *
 * A page that cannot render until two answers have arrived used to nest two
 * boundaries - the second inside the first's ready arm - and the nesting is
 * silently a THIRD behaviour on top of the two it composes. Both fetches start
 * together (the hooks run unconditionally at the top of the component); it is
 * the inner boundary that is not MOUNTED until the outer one resolves, so its
 * `useStalled` effect - the patience deadline, `RESOLVE_PATIENCE_MS` - only
 * starts then. A screen whose first read takes eight seconds and whose second
 * never lands leaves the reader on a spinner for eight seconds PLUS the twelve,
 * and a parked second read is not reported as an error until the first has
 * finished. The deadline exists precisely so that nobody waits twice.
 *
 * So the two are combined BEFORE the phase is asked, and one boundary sits over
 * the pair. The rules, each of which is the honest reading of the pair:
 *
 *  - either failing is the pair failing, and its error is what the reader is
 *    shown - there is no half-rendered page to protect;
 *  - either PARKED is the pair parked, and parked is the state nothing will
 *    resolve (`queryPhase` turns it into an error at once, with no wait). It
 *    ranks above `fetching`, because the pair cannot complete however long the
 *    other half runs;
 *  - `success` only when both carry data, so the ready arm gets a tuple that is
 *    never half-undefined;
 *  - `refetch` calls both, because the retry button under the error card has to
 *    retry whichever half failed and the reader does not know which.
 */
export function combineQueries<A, B>(
  a: QueryLike<A> & { refetch(): unknown },
  b: QueryLike<B> & { refetch(): unknown },
): QueryLike<readonly [A, B]> & { refetch(): unknown } {
  const failed = a.status === 'error' ? a : b.status === 'error' ? b : null;
  const both =
    a.status === 'success' &&
    b.status === 'success' &&
    a.data !== undefined &&
    b.data !== undefined;
  const paused = a.fetchStatus === 'paused' || b.fetchStatus === 'paused';
  const fetching = a.fetchStatus === 'fetching' || b.fetchStatus === 'fetching';
  return {
    status: failed !== null ? 'error' : both ? 'success' : 'pending',
    fetchStatus: paused ? 'paused' : fetching ? 'fetching' : 'idle',
    data: both ? ([a.data as A, b.data as B] as const) : undefined,
    error: failed?.error ?? null,
    failureReason: a.failureReason ?? b.failureReason,
    refetch() {
      a.refetch();
      b.refetch();
    },
  };
}
